import { useQuery, useMutation, useQueryClient } from 'react-query';
import { supabase, UserPreferences } from '../lib/supabase';
import toast from 'react-hot-toast';

export const useUserPreferences = (userId?: string) => {
  const queryClient = useQueryClient();

  const { data: preferences, isLoading } = useQuery(
    ['userPreferences', userId],
    async () => {
      if (!userId) return null;
      
      const { data, error } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 is "not found"
        throw error;
      }

      return data;
    },
    {
      enabled: !!userId,
    }
  );

  const createPreferencesMutation = useMutation(
    async (preferences: Partial<UserPreferences>) => {
      if (!userId) throw new Error('User not authenticated');
      
      const { data, error } = await supabase
        .from('user_preferences')
        .insert([{
          user_id: userId,
          main_currency: preferences.main_currency || 'USD',
          email_notifications: preferences.email_notifications ?? true,
          whatsapp_notifications: preferences.whatsapp_notifications ?? false,
          reminder_days_before: preferences.reminder_days_before ?? 3,
          theme_mode: preferences.theme_mode || 'light',
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['userPreferences', userId]);
        toast.success('Preferences saved successfully!');
      },
      onError: (error) => {
        console.error('Error creating preferences:', error);
        toast.error('Failed to save preferences');
      },
    }
  );

  const updatePreferencesMutation = useMutation(
    async (updates: Partial<UserPreferences>) => {
      if (!userId) throw new Error('User not authenticated');
      
      const { data, error } = await supabase
        .from('user_preferences')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', userId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['userPreferences', userId]);
        toast.success('Preferences updated successfully!');
      },
      onError: (error) => {
        console.error('Error updating preferences:', error);
        toast.error('Failed to update preferences');
      },
    }
  );

  const savePreferences = async (updates: Partial<UserPreferences>) => {
    if (!userId) return;

    if (!preferences) {
      // Create new preferences
      await createPreferencesMutation.mutateAsync(updates);
    } else {
      // Update existing preferences
      await updatePreferencesMutation.mutateAsync(updates);
    }
  };

  // Get main currency with fallback
  const getMainCurrency = () => {
    return preferences?.main_currency || 'USD';
  };

  // Get theme mode with fallback
  const getThemeMode = () => {
    return preferences?.theme_mode || 'light';
  };

  // Get notification settings with fallbacks
  const getNotificationSettings = () => {
    return {
      email_notifications: preferences?.email_notifications ?? true,
      whatsapp_notifications: preferences?.whatsapp_notifications ?? false,
      reminder_days_before: preferences?.reminder_days_before ?? 3,
    };
  };

  return {
    preferences,
    isLoading,
    savePreferences,
    getMainCurrency,
    getThemeMode,
    getNotificationSettings,
    isUpdating: createPreferencesMutation.isLoading || updatePreferencesMutation.isLoading,
  };
}; 